import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  existInLocalStorage,
  getFromLocalStorage,
  setToLocalStorage,
} from 'wflow-cdk/utils/local-storage.utils';
import { SmartlookEventService } from 'wflow-main-app/src/app/core/services/analytics/analytic-event.service';
import { getOrgPath } from 'wflow-main-app/src/app/shared/utils/route.utils';

export const OPEN_OUT_STATE = 'OPEN_OUT_STATE';
export const OUT_W_SIZE = 'OUT_W_SIZE';
export const OUTER_HTML_CLASS = 'outer-window';

interface OuterWindowSize {
  w: number;
  h: number;
}

@Injectable({
  providedIn: 'root',
})
export class OuterWindowControlService {
  private statusDefault = false;
  private frameName = 'detail';
  private routeSubscriber: Subscription;

  status = new BehaviorSubject<boolean>(this.statusDefault);
  windowStatus = new BehaviorSubject<boolean>(this.statusDefault);
  activeElementId = new BehaviorSubject<string>(null);

  externalWindowReference: Window = null;
  isExternalWindow = window.name === this.frameName;
  outWindowSize: OuterWindowSize;

  constructor(private router: Router) {}

  init() {
    if (this.isExternalWindow) {
      document.querySelector('html').classList.add(OUTER_HTML_CLASS);
      this.sendMessage({ open: true }, window.opener);
      window.addEventListener(
        'unload',
        () => this.sendMessage({ open: false }, window.opener),
        { passive: true },
      );
      window.addEventListener('resize', this.setOutWindowSize.bind(this), {
        passive: true,
      });

      this.routeSubscriber = this.router.events.subscribe((val) => {
        if (val instanceof NavigationEnd) {
          const data = JSON.parse(JSON.stringify({ routeEvent: val }));
          this.sendMessage(data, window.opener);
        }
      });
    } else {
      window.addEventListener(
        'unload',
        () => {
          if (
            (this.externalWindowReference !== null ||
              (this.externalWindowReference &&
                !this.externalWindowReference.closed)) &&
            !this.isExternalWindow
          ) {
            this.externalWindowReference.close();
          }
        },
        { passive: true },
      );
    }

    if (existInLocalStorage(OPEN_OUT_STATE)) {
      this.status.next(getFromLocalStorage(OPEN_OUT_STATE) === 'true');
    }
    window.addEventListener('message', this.messageEvent.bind(this), {
      passive: true,
    });
  }

  destroy() {
    window.removeEventListener('message', this.messageEvent, false);
    // this.externalWindowReference.close();
    if (this.isExternalWindow) {
      this.routeSubscriber.unsubscribe();
    }
  }

  setStatus() {
    this.status.next(!this.status.value);
    setToLocalStorage(OPEN_OUT_STATE, this.status.value ? 'true' : 'false');
  }

  openDetail(id) {
    SmartlookEventService.track('Open detail in external tab');
    this.outWindowSize = getFromLocalStorage(OUT_W_SIZE) || {
      w: window.outerWidth - 100,
      h: window.outerHeight - 100,
    };

    if (this.status.value) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([getOrgPath(), 'documents', 'detail', id]),
      );
      this.activeElementId.next(id);
      if (
        this.externalWindowReference == null ||
        this.externalWindowReference.closed
      ) {
        this.externalWindowReference = window.open(
          url,
          this.frameName,
          'resizable,scrollbars,status,width=' +
            this.outWindowSize.w +
            ',height=' +
            this.outWindowSize.h,
        );
      } else {
        this.externalWindowReference.focus();
        const data = {
          route: [getOrgPath(), 'documents', 'detail', id],
        };
        this.sendMessage(data, this.externalWindowReference);
        this.sendMetadata();
      }
    }
  }

  sendMessage(data: Object | string, to: Window) {
    if (to !== null) to.postMessage(data, window.location.origin);
  }

  private sendMetadata() {
    // code to send metadata
  }

  private messageEvent(event: MessageEvent) {
    /* Outside Protection */
    if (event.origin !== window.location.origin) {
      return;
    }

    /* Parrent events */
    if (!this.isExternalWindow) {
      if (event.data.open !== undefined) {
        this.windowStatus.next(event.data.open);
        if (!event.data.open) this.activeElementId.next(null);
        else this.sendMetadata();
      }

      if (event.data.navigate !== undefined) {
        this.activeElementId.next(event.data.navigate.id);
      }

      if (event.data.routeEvent !== undefined) {
        const routeEvent = event.data.routeEvent;
        const urlArr = routeEvent.url.split('/');
        const id = urlArr[urlArr.length - 1];
        this.activeElementId.next(id);
      }
    }

    /* Child events */
    if (this.isExternalWindow) {
      this.sendMessage("I'm have your data!", window.opener);

      if (event.data.route) {
        this.router.navigate(event.data.route);
      }

      if (event.data.metadata) {
        // do something with metadata
      }
    }
  }

  private setOutWindowSize() {
    setToLocalStorage(OUT_W_SIZE, {
      w: window.outerWidth,
      h: window.outerHeight,
    });
  }
}
